import { Anrede, Bezugsrecht, Domaenenwert } from '@ps20/bff-models';
import { Adressvorschlag, App } from '@ps20-frontend/frontend-models';
import {
  AdressvorschlagFailedPayload,
  AdressvorschlagPayload,
  AngebotsId,
  CALLER_ID,
  GlobalActions,
  GlobalDeleteProduktdAction,
  GlobalProduktdAction,
  UnfCheckoutProduktPayload,
} from '@ps20-frontend/shop-integration-redux';
import dayjs from 'dayjs';
import { AnyAction } from 'redux';
import { DATEFORMAT_LOCAL_STATE } from '@ps20-frontend/utils';
import cloneDeep from 'lodash/cloneDeep';
import {
  CheckoutActions,
  UnfAddBezugsberechtigterAction,
  UnfallhilfeSofortBesprochenAction,
  UnfBezugsrechtChangedAction,
  UnfDatenschutzEinwilligungChangedAction,
  UnfDeleteBezugsberechtigterAction,
  UnfDynamikChangedAction,
  UnfKisBeeintraechtigungenAction,
  UnfKisFrageJaNeinChangedAction,
  UnfKisUnterlagenWerdenEingereichtAction,
  UnfPflegeFrageChangedAction,
} from '../checkout.actions';
import { calculateVBeginnDatum } from './produkte.reducer';

export interface Bezugsberechtigter {
  kdvId: string;
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  anteil: number;
  anrede: Anrede;
  akadgrad?: Domaenenwert;
  adelstitel?: Domaenenwert;
  adelsbez?: Domaenenwert;
  adresse: Adresse;
}

export interface Adresse {
  strasse: string;
  hausnummer: string;
  plz: string;
  ort: string;
  land?: Domaenenwert;
}
export interface BezugsberechtigterModal {
  vorname?: string;
  nachname?: string;
  anrede?: Anrede;
  akadgrad?: Domaenenwert;
  adelstitel?: Domaenenwert;
  adelsbez?: Domaenenwert;
  namenszusatz?: Domaenenwert;
  famstand?: Domaenenwert;
  geburtsdatum?: string;
  staatsbschaft?: Domaenenwert;
  heimatort?: string;
  geburtsland?: Domaenenwert;
  adresse?: Adresse;
  staatsangehoerigkeiten?: Domaenenwert[];
  adressvorschlag?: Adressvorschlag;
}

export interface VpAngaben {
  angebotsId: AngebotsId['angebotsId'];
  dynamikSelected?: boolean;
  dynamikDisabled?: boolean;
  dynamikShowInfoAlter?: boolean;
  bezugsrecht?: Bezugsrecht;
  bezugsberechtigten?: Bezugsberechtigter[];
  pflegeFrageAntwort?: boolean;
  kisSelected?: boolean;
  hasBeeintraechtigungen?: boolean;
  beeintraechtigungen?: string;
  unterlagenWerdenEingereichtAntwort?: boolean;
  isSofortschutzBesprochen?: boolean;
}

export interface UnfAngaben {
  vpAngabenListe: VpAngaben[];
  datenschutzEinwilligung: boolean;
  ausschlussList: string[];
  bezugsberechtigterModal?: BezugsberechtigterModal;
}

export const unfAngabenReducer = (sectionState: UnfAngaben = null, action: AnyAction): UnfAngaben => {
  switch (action.type) {
    case CheckoutActions.AUSSCHLUSS_LIST_RECEIVED:
      return updateAusschlussList(sectionState, action);
    case GlobalActions.PRODUKT_ACTION:
      return initVpAngaben(sectionState, action as GlobalProduktdAction);
    case GlobalActions.PRODUKT_DELETED:
      return deleteVpAngaben(sectionState, action as GlobalDeleteProduktdAction);
    case CheckoutActions.UNF_DYNAMIK_CHANGED:
      return updateDynamik(sectionState, action as UnfDynamikChangedAction);
    case CheckoutActions.UNF_BEZUGSRECHT_CHANGED:
      return updateBezugsrecht(sectionState, action as UnfBezugsrechtChangedAction);
    case CheckoutActions.UNF_DATENSCHUTZEINWILLIGUNG_CHANGED:
      return updateDatenschutzEinwilligung(
        sectionState,
        action as UnfDatenschutzEinwilligungChangedAction,
      );
    case CheckoutActions.UNF_PFLEGEFRAGE_CHANGED:
      return updatePflegeFrage(sectionState, action as UnfPflegeFrageChangedAction);
    case CheckoutActions.UNF_KIS_FRAGE_CHANGED:
      return updateKISFrage(sectionState, action as UnfKisFrageJaNeinChangedAction);
    case CheckoutActions.UNF_KIS_BEEINTRAECHTIGUNGEN_CHANGED:
      return updateKISBeeintraechtigungen(sectionState, action as UnfKisBeeintraechtigungenAction);
    case CheckoutActions.UNF_IS_SOFORTSCHUTZ_BESPROCHEN_CHANGED:
      return updateUnfallhilfeSofortBesprochen(
        sectionState,
        action as UnfallhilfeSofortBesprochenAction,
      );
    case CheckoutActions.UNF_KIS_UNTERLAGENEINGEREICHT_CHANGED:
      return updateKISUnterlagen(sectionState, action as UnfKisUnterlagenWerdenEingereichtAction);
    case CheckoutActions.UNF_ADD_BEZUGSBERECHTIGTER:
      return addBezugsberechtigter(sectionState, action as UnfAddBezugsberechtigterAction);
    case CheckoutActions.UNF_EDIT_BEZUGSBERECHTIGTER:
      return editBezugsberechtigter(sectionState, action as UnfAddBezugsberechtigterAction);
    case CheckoutActions.UNF_DELETE_BEZUGSBERECHTIGTER:
      return deleteBezugsberechtigter(sectionState, action as UnfDeleteBezugsberechtigterAction);
    case CheckoutActions.UNF_UPDATE_GESUNDHEITSDATEN_AFTER_LOAD: {
      return {
        ...sectionState,
        vpAngabenListe: sectionState.vpAngabenListe.map(angaben => {
          const elementFromPayload: VpAngaben = action.payload.unfAngaben?.vpAngabenListe?.find(
            (element: VpAngaben) => element.angebotsId === angaben.angebotsId,
          );
          return {
            ...angaben,
            pflegeFrageAntwort: elementFromPayload
              ? elementFromPayload.pflegeFrageAntwort
              : angaben.pflegeFrageAntwort,
            hasBeeintraechtigungen: elementFromPayload
              ? elementFromPayload.hasBeeintraechtigungen
              : angaben.hasBeeintraechtigungen,
            beeintraechtigungen: elementFromPayload
              ? elementFromPayload.beeintraechtigungen
              : angaben.beeintraechtigungen,
          };
        }),
      };
    }
    case GlobalActions.ADDRESSVORSCHLAG_SUCCESSFUL: {
      const payload: AdressvorschlagPayload = action.payload;
      if (payload.callerId !== CALLER_ID.CHECKOUT_AZAM_BEZUGSBERECHTIGTER) {
        return sectionState;
      }
      const newState: UnfAngaben = cloneDeep(sectionState);
      newState.bezugsberechtigterModal = {
        ...newState.bezugsberechtigterModal,
        adressvorschlag: {
          orte: payload.orte,
          strassen: payload.strassen,
          lastError: null,
        },
      };
      if (payload.orte.length === 1) {
        newState.bezugsberechtigterModal.adresse = {
          ...newState.bezugsberechtigterModal.adresse,
          ort: payload.orte[0],
        };
      }
      return newState;
    }
    case GlobalActions.ADDRESSVORSCHLAG_FAILED: {
      const payload: AdressvorschlagFailedPayload = action.payload;
      if (payload.callerId !== CALLER_ID.CHECKOUT_AZAM_BEZUGSBERECHTIGTER) {
        return sectionState;
      }
      const newState: UnfAngaben = cloneDeep(sectionState);
      newState.bezugsberechtigterModal.adressvorschlag = {
        lastError: payload.reason,
        orte: [],
        strassen: [],
      };
      return newState;
    }
    case CheckoutActions.AZAM_PARTIAL_BEZUGSBERECHTIGTER_ADDRESS_UPDATE: {
      const newState: UnfAngaben = cloneDeep(sectionState);
      const addressChanges: Partial<Adresse> = action.payload;
      newState.bezugsberechtigterModal.adresse = {
        ...newState.bezugsberechtigterModal.adresse,
        ...addressChanges,
      };
      return newState;
    }
    case CheckoutActions.AZAM_BEZUGSBERECHTIGTER_PLZ_CHANGED: {
      const newState: UnfAngaben = cloneDeep(sectionState);
      const addressChanges: Partial<Adresse> = action.payload;
      newState.bezugsberechtigterModal.adressvorschlag = {
        lastError: null,
        orte: [],
        strassen: [],
      };
      newState.bezugsberechtigterModal.adresse = {
        plz: addressChanges.plz,
        strasse: '',
        hausnummer: '',
        ort: '',
        land: {
          text: 'Deutschland',
          wert: 'DE',
        },
      };
      return newState;
    }
    case CheckoutActions.AZAM_PARTIAL_BEZUGSBERECHTIGER_UPDATE: {
      const newState: UnfAngaben = cloneDeep(sectionState);
      const changes: Partial<BezugsberechtigterModal> = action.payload;
      newState.bezugsberechtigterModal = {
        ...newState.bezugsberechtigterModal,
        ...changes,
      };
      return newState;
    }
    case CheckoutActions.RESET_AZAM_BEZUGSBERECHTIGTEN_MODAL: {
      return {
        ...sectionState,
        bezugsberechtigterModal: null,
      };
    }

    default:
      return sectionState;
  }
};

function updateAusschlussList(sectionState: UnfAngaben, action: AnyAction): UnfAngaben {
  return { ...sectionState, ausschlussList: action.payload };
}

function initVpAngaben(sectionState: UnfAngaben, action: GlobalProduktdAction): UnfAngaben {
  const { appId, angebotsId } = action.payload;
  const unfCheckoutProdukt = action.payload as UnfCheckoutProduktPayload;
  if (appId === App.UNF) {
    const newVpAngabenListe = [...sectionState.vpAngabenListe];
    const _isVpOlder54 = isVpOlder54(unfCheckoutProdukt);
    const newVpAngaben = newVpAngabenListe.find(
      vnAgaben => vnAgaben.angebotsId === unfCheckoutProdukt.angebotsId,
    );
    if (!newVpAngaben) {
      newVpAngabenListe.push({
        angebotsId,
        dynamikSelected: !_isVpOlder54,
        dynamikDisabled: _isVpOlder54,
        dynamikShowInfoAlter: _isVpOlder54,
        pflegeFrageAntwort: null,
        kisSelected: unfCheckoutProdukt.unfallVp.hasKIS,
        unterlagenWerdenEingereichtAntwort: false,
        isSofortschutzBesprochen: true,
      });
    } else {
      newVpAngaben.kisSelected = unfCheckoutProdukt.unfallVp.hasKIS;
      newVpAngaben.pflegeFrageAntwort = unfCheckoutProdukt.unfallVp.hasKIS
        ? null
        : newVpAngaben?.pflegeFrageAntwort;
      newVpAngaben.hasBeeintraechtigungen = !unfCheckoutProdukt.unfallVp.hasKIS
        ? undefined
        : newVpAngaben?.hasBeeintraechtigungen;
      newVpAngaben.beeintraechtigungen = !unfCheckoutProdukt.unfallVp.hasKIS
        ? undefined
        : newVpAngaben?.beeintraechtigungen;
    }
    return {
      ...sectionState,
      vpAngabenListe: newVpAngabenListe,
    };
  }

  return sectionState;
}

function deleteVpAngaben(sectionState: UnfAngaben, action: GlobalDeleteProduktdAction): UnfAngaben {
  const angebotsIdsToDelete = new Set(
    action.payload.angebotsIds.map(angebotsIds => angebotsIds.angebotsId),
  );
  return {
    ...sectionState,
    vpAngabenListe: sectionState.vpAngabenListe.filter(
      angaben => !angebotsIdsToDelete.has(angaben.angebotsId),
    ),
  };
}

function updateDynamik(sectionState: UnfAngaben, action: UnfDynamikChangedAction): UnfAngaben {
  return {
    ...sectionState,
    vpAngabenListe: sectionState.vpAngabenListe.map(angaben => {
      if (angaben.angebotsId === action.payload.angebotsId) {
        return {
          ...angaben,
          dynamikSelected: action.payload.dynamik,
        };
      }
      return angaben;
    }),
  };
}

function updateBezugsrecht(
  sectionState: UnfAngaben,
  action: UnfBezugsrechtChangedAction,
): UnfAngaben {
  return {
    ...sectionState,
    vpAngabenListe: sectionState.vpAngabenListe.map(angaben => {
      if (angaben.angebotsId === action.payload.angebotsId) {
        return {
          ...angaben,
          bezugsrecht: action.payload.bezugsrecht,
        };
      }
      return angaben;
    }),
  };
}

function isVpOlder54(produkt: UnfCheckoutProduktPayload): boolean {
  const beginn = dayjs(calculateVBeginnDatum(produkt), DATEFORMAT_LOCAL_STATE);
  const geburtsdatum = dayjs(produkt.unfallVp.geburtsdatum, DATEFORMAT_LOCAL_STATE);
  return beginn.diff(geburtsdatum, 'years') > 54;
}

function updateDatenschutzEinwilligung(
  sectionState: UnfAngaben,
  action: UnfDatenschutzEinwilligungChangedAction,
): UnfAngaben {
  return {
    ...sectionState,
    vpAngabenListe: sectionState.vpAngabenListe.map(angaben => {
      return {
        ...angaben,
        pflegeFrageAntwort: null,
      } as VpAngaben;
    }),
    datenschutzEinwilligung: action.payload,
  };
}

function updatePflegeFrage(
  sectionState: UnfAngaben,
  action: UnfPflegeFrageChangedAction,
): UnfAngaben {
  return {
    ...sectionState,
    vpAngabenListe: sectionState.vpAngabenListe.map(angaben => {
      if (angaben.angebotsId === action.payload.angebotsId) {
        return {
          ...angaben,
          pflegeFrageAntwort: action.payload.pflegeFrageAntwort,
        };
      }
      return angaben;
    }),
  };
}

function updateKISFrage(
  sectionState: UnfAngaben,
  action: UnfKisFrageJaNeinChangedAction,
): UnfAngaben {
  return {
    ...sectionState,
    vpAngabenListe: sectionState.vpAngabenListe.map(angaben => {
      if (angaben.angebotsId === action.payload.angebotsId) {
        return {
          ...angaben,
          hasBeeintraechtigungen: action.payload.ja,
          unterlagenWerdenEingereichtAntwort: !action.payload.ja
            ? false
            : angaben.unterlagenWerdenEingereichtAntwort,
          beeintraechtigungen: !action.payload.ja ? undefined : angaben.beeintraechtigungen,
        };
      }
      return angaben;
    }),
  };
}

function updateKISBeeintraechtigungen(
  sectionState: UnfAngaben,
  action: UnfKisBeeintraechtigungenAction,
): UnfAngaben {
  return {
    ...sectionState,
    vpAngabenListe: sectionState.vpAngabenListe.map(angaben => {
      if (angaben.angebotsId === action.payload.angebotsId) {
        return {
          ...angaben,
          beeintraechtigungen: action.payload.text,
        };
      }
      return angaben;
    }),
  };
}

function updateUnfallhilfeSofortBesprochen(
  sectionState: UnfAngaben,
  action: UnfallhilfeSofortBesprochenAction,
): UnfAngaben {
  return {
    ...sectionState,
    vpAngabenListe: sectionState.vpAngabenListe.map(angaben => {
      if (angaben.angebotsId === action.payload.angebotsId) {
        return {
          ...angaben,
          isSofortschutzBesprochen: action.payload.isSofortschutzBesprochen,
        };
      }
      return angaben;
    }),
  };
}

function updateKISUnterlagen(
  sectionState: UnfAngaben,
  action: UnfKisUnterlagenWerdenEingereichtAction,
): UnfAngaben {
  return {
    ...sectionState,
    vpAngabenListe: sectionState.vpAngabenListe.map(angaben => {
      if (angaben.angebotsId === action.payload.angebotsId) {
        return {
          ...angaben,
          unterlagenWerdenEingereichtAntwort: action.payload.unterlagenAntwort,
        };
      }
      return angaben;
    }),
  };
}

function addBezugsberechtigter(
  sectionState: UnfAngaben,
  action: UnfAddBezugsberechtigterAction,
): UnfAngaben {
  return {
    ...sectionState,
    vpAngabenListe: sectionState.vpAngabenListe.map(angaben => {
      if (angaben.angebotsId === action.payload.angebotsId) {
        if (action.payload.person.kdvId == null) {
          action.payload.person = {
            ...action.payload.person,
            kdvId:
              action.payload.person.vorname +
              action.payload.person.nachname +
              action.payload.person.geburtsdatum,
          };
        }
        // Default Anteil bei der 1. bezugsberechtigten Person: "100,00"
        // Bei weiteren Personen ist der Anteil leer
        if (isFirstPerson(angaben.bezugsberechtigten)) {
          action.payload.person = { ...action.payload.person, anteil: 100 };
        } else {
          action.payload.person = { ...action.payload.person };
        }

        return {
          ...angaben,
          bezugsberechtigten: [
            ...(angaben.bezugsberechtigten
              ? angaben.bezugsberechtigten.filter(
                  person => person.kdvId != action.payload.person.kdvId,
                )
              : []),
            action.payload.person,
          ],
        };
      }
      return angaben;
    }),
  };
}

function isFirstPerson(bezugsberechtigten: Bezugsberechtigter[]): boolean {
  return !bezugsberechtigten || bezugsberechtigten.length === 0;
}

function editBezugsberechtigter(
  sectionState: UnfAngaben,
  action: UnfAddBezugsberechtigterAction,
): UnfAngaben {
  return {
    ...sectionState,
    vpAngabenListe: sectionState.vpAngabenListe.map(angaben => {
      if (angaben.angebotsId === action.payload.angebotsId) {
        return {
          ...angaben,
          bezugsberechtigten: angaben.bezugsberechtigten.map(person => {
            if (person.kdvId == action.payload.person.kdvId) {
              return action.payload.person;
            }
            return person;
          }),
        };
      }
      return angaben;
    }),
  };
}

function deleteBezugsberechtigter(
  sectionState: UnfAngaben,
  action: UnfDeleteBezugsberechtigterAction,
): UnfAngaben {
  return {
    ...sectionState,
    vpAngabenListe: sectionState.vpAngabenListe.map(angaben => {
      if (angaben.angebotsId === action.payload.angebotsId) {
        return {
          ...angaben,
          bezugsberechtigten: [
            ...angaben.bezugsberechtigten.filter(person => person.kdvId != action.payload.kdvId),
          ],
        };
      }
      return angaben;
    }),
  };
}
